import { useEffect, useState } from 'react';
import { Bell, ChevronRight, FolderKanban, Key, Play, ShoppingCart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUi } from '../stores/ui.js';
import { useNotifications } from '../hooks/useNotifications.js';
import { useNativePush } from '../hooks/useNativePush.js';
import { inNativeShell, nativePushAvailable } from '../lib/native.js';
import { Sheet } from '../ui/Sheet.js';
import {
  getCookiesStatus,
  getLafourcheStatus,
  getScraperJwtStatus,
  regenerateScraperJwt,
  triggerScraperRun,
  type CookiesStatus,
  type LafourcheCredentialsStatus,
  type ScraperJwtStatus,
  type ScraperStore,
} from '../api/carrefour.js';
import { STORE_LABELS } from '../lib/store.js';
import { teinte } from '../lib/teinte.js';

export function SettingsModal(): React.ReactElement {
  const open = useUi((s) => s.settingsOpen);
  const close = useUi((s) => s.closeSettings);
  const openNotifications = useUi((s) => s.openNotifications);
  const openCarrefourCookies = useUi((s) => s.openCarrefourCookies);
  const openLafourcheCredentials = useUi((s) => s.openLafourcheCredentials);
  const navigate = useNavigate();
  const { enabled, isSupported } = useNotifications();
  // Dans la coque Android, l'état vient du push natif (spec 18), pas du PushManager absent.
  const { status: nativePush } = useNativePush(open && nativePushAvailable);
  const pushDetail = inNativeShell
    ? nativePush?.subscribed
      ? 'On'
      : 'Off'
    : isSupported
      ? enabled
        ? 'On'
        : 'Off'
      : 'Not supported';
  const [cookiesStatus, setCookiesStatus] = useState<CookiesStatus | null>(null);
  const [lafourcheStatus, setLafourcheStatus] = useState<LafourcheCredentialsStatus | null>(null);
  const [jwtStatus, setJwtStatus] = useState<ScraperJwtStatus | null>(null);
  const [regenerating, setRegenerating] = useState(false);
  const [jwtMessage, setJwtMessage] = useState<string | null>(null);
  // Un état par magasin : les deux runs sont indépendants côté daemon, l'UI doit le
  // refléter plutôt que de bloquer les deux boutons ensemble.
  const [triggering, setTriggering] = useState<ScraperStore | null>(null);
  const [triggerMessage, setTriggerMessage] = useState<Partial<Record<ScraperStore, string>>>({});

  useEffect(() => {
    if (!open) return;
    setJwtMessage(null);
    getCookiesStatus()
      .then(setCookiesStatus)
      .catch(() => setCookiesStatus({ updatedAt: null, daysAgo: null }));
    getScraperJwtStatus()
      .then(setJwtStatus)
      .catch(() => setJwtStatus({ issuedAt: null, daysUntilExpiry: null }));
    getLafourcheStatus()
      .then(setLafourcheStatus)
      .catch(() => setLafourcheStatus({ refreshTokenUpdatedAt: null, cartIdUpdatedAt: null }));
  }, [open]);

  async function regenerate(): Promise<void> {
    if (regenerating) return;
    setRegenerating(true);
    setJwtMessage(null);
    try {
      const r = await regenerateScraperJwt();
      const expiry = new Date(r.expiresAt).toLocaleDateString('fr-FR');
      setJwtMessage(`Token régénéré · expire ${expiry}`);
      setJwtStatus({ issuedAt: r.issuedAt, daysUntilExpiry: 365 });
    } catch (err) {
      setJwtMessage(err instanceof Error ? `Erreur : ${err.message}` : 'Erreur inconnue');
    } finally {
      setRegenerating(false);
    }
  }

  async function trigger(store: ScraperStore): Promise<void> {
    if (triggering) return;
    setTriggering(store);
    setTriggerMessage((m) => ({ ...m, [store]: undefined }));
    try {
      await triggerScraperRun(store);
      setTriggerMessage((m) => ({ ...m, [store]: 'Run lancé · push notif dans ~1 min' }));
    } catch (err) {
      const msg = err instanceof Error ? `Erreur : ${err.message}` : 'Erreur inconnue';
      setTriggerMessage((m) => ({ ...m, [store]: msg }));
    } finally {
      setTriggering(null);
    }
  }

  return (
    <Sheet open={open} onClose={close} title="Settings">
      <div className="pile">
        <Section title="Organisation">
          <Entry
            icon={<FolderKanban size={18} aria-hidden />}
            title="Projets"
            chevron
            onClick={() => {
              close();
              navigate('/projets');
            }}
          />
        </Section>

        <Section title="Notifications">
          <Entry
            icon={<Bell size={18} aria-hidden />}
            title="Push notifications"
            detail={pushDetail}
            chevron
            onClick={() => {
              close();
              openNotifications();
            }}
          />
        </Section>

        <Section title="Scrapers">
          <Entry
            icon={<ShoppingCart size={18} aria-hidden />}
            title="Refresh cookies"
            detail={
              cookiesStatus === null
                ? 'Chargement…'
                : cookiesStatus.daysAgo === null
                  ? 'Jamais — paste depuis Cookie-Editor'
                  : cookiesStatus.daysAgo === 0
                    ? "Mis à jour aujourd'hui"
                    : cookiesStatus.daysAgo === 1
                      ? 'Mis à jour hier'
                      : `Mis à jour il y a ${cookiesStatus.daysAgo} jours${cookiesStatus.daysAgo > 25 ? ' ⚠️' : ''}`
            }
            chevron
            onClick={() => {
              close();
              openCarrefourCookies();
            }}
          />
          <Entry
            icon={<Key size={18} aria-hidden />}
            title={regenerating ? 'Régénération…' : 'Regenerate scraper token'}
            detail={
              jwtMessage ??
              (jwtStatus === null
                ? 'Chargement…'
                : jwtStatus.daysUntilExpiry === null
                  ? 'Jamais généré — clique pour créer'
                  : jwtStatus.daysUntilExpiry < 30
                    ? `Expire dans ${jwtStatus.daysUntilExpiry} jours ⚠️`
                    : `Expire dans ${jwtStatus.daysUntilExpiry} jours`)
            }
            disabled={regenerating}
            onClick={() => void regenerate()}
          />
          <Entry
            icon={<ShoppingCart size={18} aria-hidden />}
            title="Identifiants La Fourche"
            detail={
              lafourcheStatus === null
                ? 'Chargement…'
                : lafourcheStatus.refreshTokenUpdatedAt === null
                  ? 'Jamais enregistrés — le scraper ne peut pas tourner'
                  : `Jeton mis à jour le ${new Date(lafourcheStatus.refreshTokenUpdatedAt).toLocaleDateString('fr-FR')}`
            }
            chevron
            onClick={() => {
              close();
              openLafourcheCredentials();
            }}
          />

          {/* Un bouton par enseigne, chacun nommant sa cible. Pas un bouton unique qui
              suivrait le magasin choisi sur /courses : Settings est une modale, et une
              action qui remplit un vrai panier ne se déduit pas d'un état posé
              ailleurs (ADR 0012 § 6). */}
          {(['carrefour', 'lafourche'] as const).map((store) => (
            <Entry
              key={store}
              icon={<Play size={18} aria-hidden />}
              title={triggering === store ? 'Démarrage…' : `Run now · ${STORE_LABELS[store]}`}
              detail={
                triggerMessage[store] ?? `Lance le scraper ${STORE_LABELS[store]} immédiatement`
              }
              disabled={triggering !== null}
              onClick={() => void trigger(store)}
            />
          ))}
        </Section>

        <Section title="About">
          <p className="discret">
            Nota · personal todo app
            <br />
            nota.lyodge.com
          </p>
        </Section>
      </div>
    </Sheet>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <section className="pile pile-serree">
      <h3 className="mention">{title}</h3>
      <div className="liste liste-aeree">{children}</div>
    </section>
  );
}

// `.ligne-bordee` du kit : une entrée de réglage est un objet qu'on ouvre ou qu'on déclenche.
function Entry({
  icon,
  title,
  detail,
  chevron = false,
  disabled = false,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  detail?: string;
  chevron?: boolean;
  disabled?: boolean;
  onClick: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="ligne ligne-bordee ligne-cliquable disabled:opacity-60"
    >
      <span className="vignette vignette-petite" style={teinte('var(--encre-discrete)')}>
        {icon}
      </span>
      <span className="ligne-corps">
        <span className="ligne-titre">{title}</span>
        {detail && <span className="ligne-detail">{detail}</span>}
      </span>
      {chevron && <ChevronRight size={16} aria-hidden className="shrink-0 text-muted" />}
    </button>
  );
}
